import { Button, Dropdown, Checkbox } from 'antd'
import { CloseOutlined, InsertRowRightOutlined } from '@ant-design/icons'
import { useState, useMemo, useEffect } from 'react'
import { useTableContext } from '../contexts/table-context'
import styles from './columns-picker.module.scss'
import React from 'react'
import { Tooltip } from '../../../tooltips/default'

interface ColumnsPickerProps {
  buttonIcon?: React.ReactNode
  disabled?: boolean
}

export function ColumnsPicker<T = any>({
  buttonIcon = <InsertRowRightOutlined />,
  disabled = false,
}: ColumnsPickerProps) {
  const { visibleColumns, onColumnsSelected, preferences, disabledPickerColumns } = useTableContext<T>()
  const [open, setOpen] = useState(false)

  // Get initial visible columns (not hidden)
  const initialVisibleKeys = useMemo(
    () => visibleColumns.map(col => (col.hidden ? null : col.key)).filter((val): val is React.Key => val !== null),
    [visibleColumns],
  )

  const [tempVisibleKeys, setTempVisibleKeys] = useState<React.Key[]>(initialVisibleKeys)

  // Reset temp state when columns change
  useEffect(() => {
    setTempVisibleKeys(initialVisibleKeys)
  }, [initialVisibleKeys])

  const options = useMemo(
    () =>
      visibleColumns.map(col => ({
        label: col.title as string,
        value: col.key,
        disabled: disabledPickerColumns?.includes(col.key as string),
      })),
    [visibleColumns, disabledPickerColumns],
  )

  const handleApply = () => {
    setOpen(false)
    onColumnsSelected(tempVisibleKeys)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleResetToDefaults = () => {
    setOpen(false)
    if (preferences) {
      preferences.resetPreferences()
    }

    // Reset to all columns visible (same pattern as table-settings)
    const allKeys = visibleColumns.map(col => col.key)

    // Apply the reset immediately (don't wait for dropdown close)
    onColumnsSelected(allKeys)

    // Update temp state
    setTempVisibleKeys(allKeys)
  }

  const handleSelectAllChange = (checked: boolean) => {
    if (checked) {
      const allKeys = visibleColumns.map(col => col.key)
      setTempVisibleKeys(allKeys)
    } else {
      // Keep only disabled columns selected when unchecking "Select All"
      const disabledKeys = visibleColumns
        .filter(col => disabledPickerColumns?.includes(col.key as string))
        .map(col => col.key)
      setTempVisibleKeys(disabledKeys)
    }
  }

  const allSelected = tempVisibleKeys.length === visibleColumns.length
  const someSelected = tempVisibleKeys.length > 0 && tempVisibleKeys.length < visibleColumns.length

  const dropdownRender = () => (
    <div className={styles.dropdown}>
      <Header onClose={() => setOpen(false)} />
      <SelectAll checked={allSelected} indeterminate={someSelected} onChange={handleSelectAllChange} />
      <Body options={options} tempVisibleKeys={tempVisibleKeys} setTempVisibleKeys={setTempVisibleKeys} />
      <Footer
        onClose={handleClose}
        onApply={handleApply}
        onResetToDefaults={handleResetToDefaults}
        hasPreferences={!!preferences}
      />
    </div>
  )

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      popupRender={dropdownRender}
      trigger={['click']}
      disabled={disabled}
      placement='bottomRight'
    >
      <Tooltip title='Column Settings' placement='topLeft'>
        <Button icon={buttonIcon} disabled={disabled} variant='outlined' color={open ? 'blue' : 'default'} />
      </Tooltip>
    </Dropdown>
  )
}

interface HeaderProps {
  onClose: () => void
}

interface SelectAllProps {
  checked: boolean
  indeterminate: boolean
  onChange: (checked: boolean) => void
}

interface BodyProps {
  options: { label: string; value: React.Key; disabled?: boolean }[]
  tempVisibleKeys: React.Key[]
  setTempVisibleKeys: (keys: React.Key[]) => void
}

interface FooterProps {
  onClose: () => void
  onApply: () => void
  onResetToDefaults: () => void
  hasPreferences: boolean
}

const Header = ({ onClose }: HeaderProps) => (
  <div className={styles.header}>
    <span className={styles.headerTitle}>Column Settings</span>
    <Button type='text' size='small' icon={<CloseOutlined />} onClick={onClose} className={styles.closeButton} />
  </div>
)

const SelectAll = ({ checked, indeterminate, onChange }: SelectAllProps) => (
  <div className={styles.selectAll}>
    <Checkbox checked={checked} indeterminate={indeterminate} onChange={e => onChange(e.target.checked)}>
      Select All Columns
    </Checkbox>
  </div>
)

const Body = ({ options, tempVisibleKeys, setTempVisibleKeys }: BodyProps) => (
  <div className={styles.body}>
    <Checkbox.Group
      options={options}
      value={tempVisibleKeys}
      onChange={setTempVisibleKeys}
      className={styles.checkboxGroup}
    />
  </div>
)

const Footer = ({ onClose, onApply, onResetToDefaults, hasPreferences }: FooterProps) => (
  <div className={styles.footer}>
    {hasPreferences && (
      <Button size='small' danger variant='outlined' onClick={onResetToDefaults}>
        Reset to Defaults
      </Button>
    )}
    <div className={styles.footerActions}>
      <Button size='small' onClick={onClose}>
        Close
      </Button>
      <Button size='small' type='primary' onClick={onApply}>
        Apply
      </Button>
    </div>
  </div>
)
